/**
 * Copyright Highway9 Networks Inc.
 */
import { dedupePromise } from "~/helpers/config/concurrentDedupe";
import { proxy, releaseProxy } from "comlink";
import { PatchBatchCoordinator, type PatchWorker } from "./patchBatchCoordinator";

const patchWorker = new ComlinkWorker<typeof import("./workers")>(
    new URL("./workers", import.meta.url), { type: "module" });

const patchBatchCoordinator = new PatchBatchCoordinator(patchWorker as unknown as PatchWorker);

/**
 * Public patch worker API.
 * - `patch` — transparently batched (websocket eventContext listeners need not change).
 * - `updateEditableKPITemplate` — direct Comlink call (KPI editor, not batched).
 */
export const workerInstance = {
  patch: <T,>(data: T[], patches: Parameters<typeof patchWorker.patch>[1]) =>
    patchBatchCoordinator.patch(data, patches),
  updateEditableKPITemplate: patchWorker.updateEditableKPITemplate.bind(patchWorker),
};

/**
 * The `apiWrapper` function in TypeScript is an asynchronous function that wraps API calls using a web
 * worker and Comlink for improved performance and error handling.
 * @param cb - The `cb` parameter in the `apiWrapper` function is a callback function that is expected
 * to return a value of type `T`. This callback function is passed to the `apiWorker.fetchData` method
 * to fetch data using a web worker.
 * @returns The `apiWrapper` function is returning the result of fetching data from the API worker
 * after executing the provided callback function `cb`.
 */
export async function apiWrapper<T>(cb: () => T) {
    // Create the worker
    const apiWorker = new ComlinkWorker<typeof import("./apiWorker")>(
        new URL("./apiWorker", import.meta.url), { type: "module" });


    try {
        // Fetch the data
        const result = await apiWorker.fetchData(proxy(cb)) as T;
        // Return the result
        return result;
    }
    catch (error) {
        // Handle the error
        console.error(error);
        throw error;
    }
    finally {
        // Terminate the worker
        apiWorker.terminate();

        // Release the Comlink proxy
        apiWorker[releaseProxy]();
    }
}

type FetchWorker = typeof import("./fetchWorker");
type FetchWorkerReturnTypes = {
    [K in keyof FetchWorker]: FetchWorker[K] extends (...args: any[]) => Promise<infer R> ? R : never;
  };

/** Coalesces concurrent `apiFetch` calls (worker `fetch` bypasses main-thread `configureFetch`). */
const inFlightApiFetch = new Map<string, Promise<unknown>>();

function apiFetchKey(workerKey: string, args: unknown[]): string {
  try {
    return `${workerKey}:${JSON.stringify(args)}`;
  } catch {
    return `${workerKey}:${args.length}`;
  }
}

// Shared fetch worker (MCS-3969). apiFetch used to spawn a NEW dedicated worker for every
// call; the fetch-worker chunk bundles moment + react-dom-server, so each spawn re-parsed
// and re-initialized the whole bundle. Graph transforms fire on every metrics poll, so a
// performance trace showed ~2 CPU cores spent almost entirely on repeated worker module
// evaluation. Reuse one worker across calls (comlink multiplexes concurrent requests) and
// reap it after a minute of inactivity so it holds no memory while idle.
const FETCH_WORKER_IDLE_MS = 60_000;
function createFetchWorker() {
  return new ComlinkWorker<FetchWorker>(new URL("./fetchWorker", import.meta.url), {
    type: "module",
  });
}
let sharedFetchWorker: ReturnType<typeof createFetchWorker> | null = null;
let fetchWorkerInFlight = 0;
let fetchWorkerIdleTimer: ReturnType<typeof setTimeout> | undefined;

function scheduleFetchWorkerReap() {
  clearTimeout(fetchWorkerIdleTimer);
  fetchWorkerIdleTimer = setTimeout(() => {
    if (fetchWorkerInFlight > 0 || !sharedFetchWorker) return;
    const worker = sharedFetchWorker;
    sharedFetchWorker = null;
    worker.terminate();
    worker[releaseProxy]();
  }, FETCH_WORKER_IDLE_MS);
}

/**
 * Runs a `fetchWorker` method in the shared dedicated worker; concurrent identical calls
 * share one run, and the worker is recycled across calls (terminated only after idling).
 */
export const apiFetch = async <K extends keyof FetchWorker>(
  key: K,
  ...args: Parameters<FetchWorker[K]>
): Promise<FetchWorkerReturnTypes[K]> => {
  const dedupeKey = apiFetchKey(String(key), args);
  return dedupePromise(dedupeKey, inFlightApiFetch, async () => {
    if (!sharedFetchWorker) {
      sharedFetchWorker = createFetchWorker();
    }
    const apiWorker = sharedFetchWorker;
    fetchWorkerInFlight++;
    try {
      const method = apiWorker[key] as (...a: unknown[]) => Promise<unknown>;
      const result = await method(...args);
      return result as FetchWorkerReturnTypes[K];
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      fetchWorkerInFlight--;
      scheduleFetchWorkerReap();
    }
  }) as Promise<FetchWorkerReturnTypes[K]>;
};

